import React from 'react';
import { useTranslation } from 'react-i18next';
import { useFormatters } from '../../../utils/format';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from 'recharts';

/**
 * QuartileChart, Art. 9(f)
 * Shows the gender split across each pay quartile. The key insight this
 * visualizes: when Q1 is dominated by women and Q4 by men, it's a vertical
 * segregation signal that contributes to the headline gap.
 */

const CustomTooltip = ({ active, payload, label, t, num }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-popover border border-border rounded-md p-3 shadow-elevated min-w-[200px]">
      <p className="text-xs font-semibold text-popover-foreground mb-2">{label}</p>
      <div className="space-y-1 text-xs">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-sm bg-teal-500" />
            <span className="text-muted-foreground">{t('dashboard.quartile.female')}</span>
          </div>
          <span className="font-semibold tabular-nums text-foreground">{d.femalePct}%</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-sm bg-slate-400" />
            <span className="text-muted-foreground">{t('dashboard.quartile.male')}</span>
          </div>
          <span className="font-semibold tabular-nums text-foreground">{d.malePct}%</span>
        </div>
        <div className="pt-1.5 mt-1.5 border-t border-border flex items-center justify-between gap-4">
          <span className="text-muted-foreground">{t('dashboard.quartile.headcountTooltip')}</span>
          <span className="font-semibold tabular-nums text-foreground">
            {num(d.headcount)}
          </span>
        </div>
      </div>
    </div>
  );
};

const QuartileChart = ({ data, title }) => {
  const { t } = useTranslation();
  const { num } = useFormatters();
  // Transform data for stacked bar
  const chartData = data.map((d) => ({
    name: d.name,
    Female: d.femalePct,
    Male: d.malePct,
    femalePct: d.femalePct,
    malePct: d.malePct,
    headcount: d.headcount,
  }));

  // Interpretation summary
  const q1Female = data[0].femalePct;
  const q4Female = data[3].femalePct;
  const segregation = q1Female - q4Female;

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="px-5 py-4 border-b border-border">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-sm font-semibold text-foreground">
                {title || t('dashboard.quartile.title')}
              </h3>
              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-muted text-muted-foreground uppercase tracking-wide">
                Art. 9(f)
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {segregation > 15
                ? t('dashboard.quartile.subtitleSegregated', { pp: segregation.toFixed(0) })
                : segregation > 5
                ? t('dashboard.quartile.subtitleModerate')
                : t('dashboard.quartile.subtitleParity')}
            </p>
          </div>
          <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-teal-500" />
              <span>{t('dashboard.quartile.female')}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-slate-400" />
              <span>{t('dashboard.quartile.male')}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="p-2 pb-4">
        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 16, right: 16, left: 0, bottom: 0 }} layout="vertical">
              <CartesianGrid strokeDasharray="2 4" stroke="var(--color-border)" horizontal={false} />
              <XAxis
                type="number"
                stroke="var(--color-muted-foreground)"
                fontSize={10}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v) => `${v}%`}
                domain={[0, 100]}
              />
              <YAxis
                type="category"
                dataKey="name"
                stroke="var(--color-muted-foreground)"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                width={90}
              />
              <Tooltip content={<CustomTooltip t={t} num={num} />} cursor={{ fill: 'var(--color-muted)', opacity: 0.5 }} />
              <Bar dataKey="Female" stackId="a" fill="#14B8A6" radius={[0, 0, 0, 0]}>
                {chartData.map((_, i) => (
                  <Cell key={i} />
                ))}
              </Bar>
              <Bar dataKey="Male" stackId="a" fill="#94A3B8" radius={[0, 0, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default QuartileChart;
